/**
 * "Latest request wins" sequencing for a view that reloads when its key changes
 * (a detail page whose id input changes while it stays mounted, a drawer that is
 * opened and closed while a fetch is in flight).
 *
 * Every `Run` takes a ticket. Only the newest ticket may report `current`; an
 * earlier `Run` that settles later (value or failure) reports `stale`, and the
 * caller must not touch its state. `Loading` is true from the newest `Run`'s
 * start until that same `Run` settles, so an older response can never clear the
 * flag of a newer load.
 */

/** How one `LatestRequest.Run` settled: the newest load's value, a stale (superseded) load, or a failure. */
export type LatestRequestOutcome<T> =
    | { readonly Status: 'current'; readonly Value: T }
    | { readonly Status: 'stale' }
    | { readonly Status: 'failed'; readonly Error: Error };

/** "Latest request wins" sequencing for a view that reloads when its key changes; see the module comment. */
export class LatestRequest<TKey> {
    private ticket = 0;
    private requestedKey: TKey | null = null;
    private hasRequested = false;
    private loading = false;

    /** True while the newest `Run` has not settled. */
    get Loading(): boolean {
        return this.loading;
    }

    /** The key of the newest `Run`; null until the first one. */
    get RequestedKey(): TKey | null {
        return this.requestedKey;
    }

    /** True when `key` differs from the last requested key (what a LoadIfChanged guard needs). */
    HasChanged(key: TKey): boolean {
        return !this.hasRequested || key !== this.requestedKey;
    }

    /**
     * Fetches for `key`. Never rejects: a thrown or rejected fetch settles as `failed`
     * (a non-Error throw is wrapped), unless a newer `Run` or `Cancel` superseded it,
     * in which case it settles `stale`. `fetch` receives the captured `key`; use it,
     * not the caller's current input.
     */
    async Run<T>(key: TKey, fetch: (key: TKey) => Promise<T>): Promise<LatestRequestOutcome<T>> {
        const ticket = ++this.ticket;
        this.requestedKey = key;
        this.hasRequested = true;
        this.loading = true;
        try {
            const value = await fetch(key);
            if (ticket !== this.ticket) return { Status: 'stale' };
            this.loading = false;
            return { Status: 'current', Value: value };
        } catch (thrown: unknown) {
            // unknown: catch-boundary parameter; anything can be thrown, and it is narrowed to Error below.
            if (ticket !== this.ticket) return { Status: 'stale' };
            this.loading = false;
            return { Status: 'failed', Error: thrown instanceof Error ? thrown : new Error(String(thrown), { cause: thrown }) };
        }
    }

    /** Makes any in-flight `Run` settle `stale` and clears `Loading`. */
    Cancel(): void {
        this.ticket++;
        this.loading = false;
    }
}
